/**
 * The board's routes: `/DWLF-<n>` addresses a ticket, `/epic/<name>` addresses an epic,
 * and everything else is the board.
 *
 * Hand-rolled rather than a router. Two patterns still do not justify a dependency, and
 * the whole of it is `pushState` plus `popstate`.
 *
 * React-free so the parsing is testable under the root runner with no DOM and no web
 * install — the same reason `viewPrefs.ts` and `freshness.ts` are shaped this way.
 */

export type Route =
  | { kind: 'board' }
  | { kind: 'ticket'; ref: number }
  | { kind: 'epic'; name: string };

export const BOARD: Route = { kind: 'board' };

/**
 * Case-insensitive, and a trailing slash is tolerated: these arrive pasted out of chat,
 * commit messages and vault notes, where `DWLF-12` is written every way people write it.
 * What is WRITTEN is always canonical — see `pathForRoute`.
 */
const TICKET_PATH = /^\/dwlf-(\d+)\/?$/i;

/** Epic names carry spaces and ampersands, so the segment arrives percent-encoded. */
const EPIC_PATH = /^\/epic\/([^/]+)\/?$/i;

export function parseRoute(path: string): Route {
  const ticket = TICKET_PATH.exec(path);
  if (ticket) {
    const ref = Number(ticket[1]);
    // Refs are positive integers (charter I2). `Number` would happily return 1e21 for a
    // long enough digit string, and that must not reach the API as a ticket ref.
    return Number.isSafeInteger(ref) && ref > 0 ? { kind: 'ticket', ref } : BOARD;
  }

  const epic = EPIC_PATH.exec(path);
  if (!epic) return BOARD;
  try {
    const name = decodeURIComponent(epic[1]);
    // A name is not optional and not blank; an un-decodable escape is a mistyped link,
    // and both land on the board rather than on a request that cannot succeed.
    return name.trim() ? { kind: 'epic', name } : BOARD;
  } catch {
    return BOARD;
  }
}

/** The canonical path for a route. */
export function pathForRoute(route: Route): string {
  if (route.kind === 'ticket') return `/DWLF-${route.ref}`;
  if (route.kind === 'epic') return `/epic/${encodeURIComponent(route.name)}`;
  return '/';
}

/** Whether two routes address the same thing — what decides push versus replace. */
export const sameRoute = (a: Route, b: Route): boolean =>
  a.kind === b.kind &&
  (a.kind !== 'ticket' || a.ref === (b as { ref: number }).ref) &&
  (a.kind !== 'epic' || a.name === (b as { name: string }).name);

/** The ticket a path addresses, or null for anything else. */
export function refFromPath(path: string): number | null {
  const route = parseRoute(path);
  return route.kind === 'ticket' ? route.ref : null;
}

/** The canonical path for a ticket, or the board. */
export const pathForRef = (ref: number | null): string =>
  pathForRoute(ref === null ? BOARD : { kind: 'ticket', ref });

/**
 * Whether what is on screen is something other than the board — i.e. whether choosing a
 * board control (a tab, a filter, a saved-view chip) has to navigate before its effect
 * would be visible.
 *
 * Those controls change BOARD state, so on any other page they change something nobody
 * can see: the click is recorded and not obeyed, and the choice reappears on the next
 * refresh as one you never made.
 *
 * Asked about the BASE route — the page a ticket drawer would be sitting over — not about
 * the drawer itself. So a control clicked while the board is already showing changes
 * nothing about the route, and an open ticket stays open. It is not a promise that a
 * ticket survives: leaving an epic page navigates, and navigating closes the drawer.
 */
export const isAwayFromBoard = (showing: Route): boolean => showing.kind !== 'board';
